import { Matrix3, Vector2 } from 'three';

import { split } from '@spazious/dollhouse-selector';
import {
  createUpdateNodeAction,
  FShallowNodeTypesEnum,
  GraphAction,
} from '@spazious/dollhouse-shallow-graph';
import { getCenter } from '@spazious/dollhouse-three';

import { Commander, CommanderActionTypesEnum } from '../types.js';

import { GraphNodeAction } from './GraphNodeAction.js';

export type RotateActionPayload = {
  /**
   * Rotation angle in radians
   */
  angle: number;
  /**
   * Treat the nodes as a group for moving them together with its relative position
   * If group is set to false, the rotation will be applied to each node individually
   * @default true
   */
  group?: boolean;
};

const _center = new Vector2();
const _rmat = new Matrix3();
const _pos = new Vector2();

export class RotateAction extends GraphNodeAction<
  CommanderActionTypesEnum.Rotate,
  RotateActionPayload
> {
  constructor(commander: Commander, selector: string, payload?: RotateActionPayload) {
    super(commander, CommanderActionTypesEnum.Rotate, selector, payload);
  }

  _apply(): void {
    const { staging, prevState } = this.commander;

    if (!this.payload) {
      throw new Error('Payload is required for RotateAction');
    }

    const { angle, group = true } = this.payload;

    const selectors = split(this.selector());
    const asGroup = selectors.length > 1 && group;

    staging.abort();

    if (asGroup) {
      // calculate group center
      const points = selectors.reduce((acc, selector) => {
        const node = prevState.nodes[selector];

        if (!node) {
          throw new Error(`Node with selector ${selector} not found`);
        }

        if (
          node.type !== FShallowNodeTypesEnum.FGroup &&
          node.parent &&
          selectors.includes(node.parent)
        ) {
          // consider only the parent nodes if both parent and stacked children are selected
          return acc;
        }

        acc.push(new Vector2().fromArray((node as any).position ?? [0, 0]));

        return acc;
      }, [] as Vector2[]);

      getCenter(points, _center);
      _rmat.makeRotation(-angle);
    }

    const actions: GraphAction[] = selectors.reduce((acc, selector) => {
      const node = prevState.nodes[selector];
      if (asGroup) {
        const point = _pos.fromArray((node as any).position ?? [0, 0]);

        // rotate position around common group center
        const position = point.sub(_center).applyMatrix3(_rmat).add(_center).toArray();
        acc.push(createUpdateNodeAction(selector, 'position', position));

        // add angle to current rotation
        acc.push(
          createUpdateNodeAction(selector, 'rotation', ((node as any).rotation ?? 0) + angle)
        );
      } else {
        // rotate individual nodes
        acc.push(createUpdateNodeAction(selector, 'rotation', angle));
      }
      return acc;
    }, [] as GraphAction[]);

    staging.add(actions);
  }
}
